/**
 * TestRunnerModal — step-by-step test procedure walker.
 *
 * Verbatin port of the standalone `TestRunnerModal.tsx` with:
 *  - Bonus A: bad-bottle detection before saving
 *  - Bonus B: "Raise to ideal" / "Lower to ideal" button
 *  - Bonus D: print procedure button
 *  - Bonus E: photo capture on final result
 */
import { useState, useCallback } from 'react';
import type { TestKit, InstructionStep, TestLogEntry, CalculationInputs } from '@reefapp/shared';
import { detectAnomalousResult } from '@reefapp/shared';
import { Timer } from './Timer';
import { BadBottleWarning } from './BadBottleWarning';
import { DoseFromDeltaButton } from './DoseFromDeltaButton';
import { PhotoCapture } from './PhotoCapture';

export interface TestRunnerModalProps {
  testKit: TestKit;
  logs: ReadonlyArray<TestLogEntry>;
  onClose(): void;
  onLog(entry: Omit<TestLogEntry, 'id' | 'date'>): void;
}

export function TestRunnerModal({
  testKit,
  logs,
  onClose,
  onLog,
}: TestRunnerModalProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [inputs, setInputs] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [finalResult, setFinalResult] = useState<number | string | null>(null);
  const [anomaly, setAnomaly] = useState<ReturnType<typeof detectAnomalousResult> | null>(null);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | undefined>(undefined);

  const currentStep: InstructionStep | undefined =
    testKit.instructions[stepIndex];

  const handleInputChange = (id: string, value: string) => {
    setInputs((prev) => ({ ...prev, [id]: parseFloat(value) }));
  };

  const computeResult = useCallback(() => {
    try {
      const calcInputs: CalculationInputs = { ...inputs };
      const result = testKit.calculation(calcInputs);
      setFinalResult(result);
      return result;
    } catch {
      setFinalResult('Error');
      return 'Error';
    }
  }, [testKit, inputs]);

  const handleNext = () => {
    if (currentStep?.timer && !isTimerRunning) {
      setIsTimerRunning(true);
      return;
    }
    setIsTimerRunning(false);
    if (stepIndex < testKit.instructions.length - 1) {
      setStepIndex((prev) => prev + 1);
    } else {
      computeResult();
    }
  };

  const handleTimerComplete = useCallback(() => {
    setIsTimerRunning(false);
    if (stepIndex < testKit.instructions.length - 1) {
      setStepIndex((prev) => prev + 1);
    } else {
      computeResult();
    }
  }, [stepIndex, testKit, computeResult]);

  const handleLogAttempt = () => {
    if (finalResult === null) return;

    // Bonus A: check for anomalous result before saving
    if (typeof finalResult === 'number') {
      const detection = detectAnomalousResult(
        testKit.parameterKey,
        finalResult,
        logs,
      );
      if (detection.isAnomalous) {
        setAnomaly(detection);
        return;
      }
    }

    doLog();
  };

  const doLog = () => {
    if (finalResult === null) return;
    onLog({
      kitSlug: testKit.slug,
      brand: testKit.brand,
      testName: testKit.name,
      parameter: testKit.parameter,
      parameterKey: testKit.parameterKey,
      result: finalResult,
      unit: testKit.resultUnit,
      notes: notes || undefined,
      photoDataUrl: photoDataUrl || undefined,
    });
  };

  const handleRestart = () => {
    setStepIndex(0);
    setInputs({});
    setNotes('');
    setIsTimerRunning(false);
    setFinalResult(null);
    setAnomaly(null);
    setPhotoDataUrl(undefined);
  };

  const handlePrint = () => {
    const container = document.querySelector(
      '.reefapp-test-kits-scope .test-runner-procedure',
    );
    if (container) {
      container.classList.add('printable');
    }
    window.print();
    if (container) {
      container.classList.remove('printable');
    }
  };

  const isLastStep =
    stepIndex === testKit.instructions.length - 1 && !currentStep?.timer;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between p-5 border-b border-slate-700 rounded-t">
          <div>
            <p className="text-sm font-semibold text-cyan-400">
              {testKit.brand} - {testKit.parameter}
            </p>
            <h3 className="text-2xl font-bold text-slate-100">
              {testKit.name}
            </h3>
          </div>
          <button
            className="p-1 ml-auto text-slate-400 hover:text-slate-200 text-3xl font-semibold"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="relative p-6 flex-auto overflow-y-auto test-runner-procedure">
          {finalResult === null ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-slate-400">
                  Step {stepIndex + 1} of {testKit.instructions.length}
                </h4>
                <div className="w-1/2 bg-slate-700 rounded-full h-2.5">
                  <div
                    className="bg-cyan-600 h-2.5 rounded-full"
                    style={{
                      width: `${((stepIndex + 1) / testKit.instructions.length) * 100}%`,
                    }}
                  />
                </div>
              </div>

              <div className="bg-slate-700/50 p-4 rounded-md min-h-[100px]">
                <p className="text-slate-200 text-lg">
                  {currentStep?.text}
                </p>
                {currentStep?.note && (
                  <p className="text-xs text-slate-500 mt-2 italic">
                    {currentStep.note}
                  </p>
                )}
              </div>

              {isTimerRunning && currentStep?.timer && (
                <Timer
                  duration={currentStep.timer}
                  onComplete={handleTimerComplete}
                />
              )}

              {currentStep?.input && !isTimerRunning && (
                <div className="mt-4">
                  <label
                    htmlFor={currentStep.input.id}
                    className="block text-sm font-medium text-slate-300 mb-1"
                  >
                    {currentStep.input.label}
                  </label>
                  <input
                    id={currentStep.input.id}
                    type="number"
                    min={currentStep.input.min}
                    max={currentStep.input.max}
                    step={currentStep.input.step ?? 1}
                    onChange={(e) =>
                      handleInputChange(currentStep.input!.id, e.target.value)
                    }
                    className="mt-1 block w-full pl-3 pr-3 py-2 text-base bg-slate-700 border-slate-600 text-slate-200 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md shadow-sm"
                    placeholder={`Enter ${currentStep.input.label.toLowerCase()}`}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              {/* Print button */}
              <button
                onClick={handlePrint}
                className="absolute top-2 right-2 text-xs text-slate-400 hover:text-cyan-400 flex items-center gap-1 print:hidden"
              >
                <svg
                  aria-hidden="true"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 12H4a2 2 0 00-2 2v4a2 2 0 002 2h16a2 2 0 002-2v-4a2 2 0 00-2-2h-2" />
                  <rect x="6" y="14" width="12" height="8" rx="1" />
                </svg>
                Print
              </button>

              <h4 className="text-lg font-semibold text-slate-400">
                Test Complete!
              </h4>
              <div className="my-4 p-6 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-sm text-cyan-400 font-medium">
                  {testKit.parameter}
                </p>
                <p className="text-5xl font-bold text-cyan-300">
                  {typeof finalResult === 'number'
                    ? finalResult.toFixed(2)
                    : finalResult}
                  <span className="text-3xl ml-2 font-medium text-cyan-500">
                    {testKit.resultUnit}
                  </span>
                </p>
              </div>

              {/* Bonus B: Dose from delta button */}
              {typeof finalResult === 'number' && (
                <DoseFromDeltaButton
                  parameterKey={testKit.parameterKey}
                  currentValue={finalResult}
                  unit={testKit.resultUnit}
                />
              )}

              {/* Bonus A: Anomaly banner */}
              {anomaly && (
                <BadBottleWarning
                  deltaSigma={anomaly.deltaSigma ?? 0}
                  mean={anomaly.mean ?? 0}
                  unit={testKit.resultUnit}
                  onConfirmSave={() => {
                    setAnomaly(null);
                    doLog();
                  }}
                  onRetest={handleRestart}
                />
              )}

              {/* Bonus E: Photo capture */}
              <PhotoCapture
                existingDataUrl={photoDataUrl}
                onCapture={setPhotoDataUrl}
              />

              {/* Notes */}
              <div className="mt-4">
                <label
                  htmlFor="notes"
                  className="block text-sm font-medium text-slate-300 text-left mb-1"
                >
                  Notes (optional)
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="block w-full rounded-md bg-slate-700 border-slate-600 text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                  placeholder="e.g., Noticed some algae on the glass..."
                />
              </div>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between p-4 border-t border-slate-700 rounded-b">
          {finalResult !== null ? (
            <>
              <button
                onClick={handleRestart}
                className="text-slate-400 font-bold uppercase text-sm px-6 py-3 rounded-md hover:bg-slate-700 transition-all duration-150"
              >
                Restart
              </button>
              <button
                onClick={handleLogAttempt}
                className="bg-green-600 text-white font-bold uppercase text-sm px-6 py-3 rounded-md shadow hover:shadow-lg hover:bg-green-500 transition-all duration-150"
              >
                Log Result
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="text-slate-400 font-bold uppercase text-sm px-6 py-3 rounded-md hover:bg-slate-700 transition-all duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                disabled={isTimerRunning}
                className="bg-cyan-600 text-white font-bold uppercase text-sm px-6 py-3 rounded-md shadow hover:shadow-lg hover:bg-cyan-500 disabled:bg-slate-600 disabled:cursor-not-allowed transition-all duration-150"
              >
                {currentStep?.timer && !isTimerRunning
                  ? 'Start Timer'
                  : isLastStep
                    ? 'Calculate'
                    : 'Next Step'}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
